export const INSUFFICIENT_CREDITS_CODE = 'insufficient_credits' as const;
export const CONCURRENT_PROCESSING_CODE = 'concurrent_processing' as const;
export const RATE_LIMITED_CODE = 'rate_limited' as const;

export const SAFE_INSUFFICIENT_MESSAGE = 'Insufficient credits to complete this action.';

function safeNonNegativeNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return undefined;
  }
  return value;
}

type CodedError = Error & { code?: string; status?: number };

function isCodedError(error: unknown): error is CodedError {
  return error instanceof Error;
}

function hasErrorCode(error: unknown, code: string): boolean {
  return isCodedError(error) && error.code === code;
}

function hasErrorStatus(error: unknown, status: number): boolean {
  return isCodedError(error) && error.status === status;
}

export class InsufficientCreditsError extends Error {
  readonly status = 402;
  readonly code = INSUFFICIENT_CREDITS_CODE;
  readonly required: number | undefined;
  readonly balance: number | undefined;

  constructor(options?: { required?: unknown; balance?: unknown; message?: string }) {
    super(options?.message ?? SAFE_INSUFFICIENT_MESSAGE);
    this.name = 'InsufficientCreditsError';
    this.required = safeNonNegativeNumber(options?.required);
    this.balance = safeNonNegativeNumber(options?.balance);
  }
}

export class ConcurrentProcessingError extends Error {
  readonly status = 409;
  readonly code = CONCURRENT_PROCESSING_CODE;

  constructor(message = 'Another generation is already processing. Please wait.') {
    super(message);
    this.name = 'ConcurrentProcessingError';
  }
}

export class RateLimitError extends Error {
  readonly status = 429;
  readonly code = RATE_LIMITED_CODE;

  constructor(message = 'Too many requests. Please wait and try again.') {
    super(message);
    this.name = 'RateLimitError';
  }
}

export function isInsufficientCreditsError(error: unknown): error is InsufficientCreditsError {
  if (error instanceof InsufficientCreditsError) return true;
  return hasErrorCode(error, INSUFFICIENT_CREDITS_CODE) || hasErrorStatus(error, 402);
}

export function isConcurrentProcessingError(error: unknown): error is ConcurrentProcessingError {
  if (error instanceof ConcurrentProcessingError) return true;
  return hasErrorCode(error, CONCURRENT_PROCESSING_CODE) || hasErrorStatus(error, 409);
}

export function isRateLimitError(error: unknown): error is RateLimitError {
  if (error instanceof RateLimitError) return true;
  return hasErrorCode(error, RATE_LIMITED_CODE) || hasErrorStatus(error, 429);
}

export function parseApiNumericField(value: unknown): number | undefined {
  return safeNonNegativeNumber(value);
}

export function throwFromApiErrorResponse(status: number, data: Record<string, unknown>): never {
  const safeMessage =
    typeof data.error === 'string' && data.error.length > 0 ? data.error : undefined;

  if (status === 402) {
    throw new InsufficientCreditsError({
      // Video routes may send creditCost instead of required.
      required: data.required ?? data.creditCost,
      balance: data.balance,
      message: safeMessage,
    });
  }

  if (status === 409) {
    throw new ConcurrentProcessingError(
      safeMessage ?? 'Another generation is already processing. Please wait.'
    );
  }

  if (status === 429) {
    throw new RateLimitError(safeMessage ?? 'Too many requests. Please wait and try again.');
  }

  throw new Error(safeMessage ?? `Request failed: ${status}`);
}

export interface CreditUxStrings {
  insufficient: string;
  concurrentProcessing: string;
  generationFailed: string;
  creditsRestored: string;
  upgradePricing: string;
  partialBusinessBuilder: string;
  currentBalance: string;
  generationCost: string;
  creditSingular: string;
  creditPlural: string;
}

export function creditLabel(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export function formatInsufficientCreditsMessage(
  strings: CreditUxStrings,
  required: number,
  balance: number
): string {
  const requiredLabel = creditLabel(required, strings.creditSingular, strings.creditPlural);
  return strings.insufficient
    .replace('{required}', String(required))
    .replace('{balance}', String(balance))
    .replace('{requiredLabel}', requiredLabel);
}

export function resolveCreditErrorMessage(
  error: unknown,
  strings: CreditUxStrings,
  fallback?: { required?: number; balance?: number }
): { message: string; showPricing: boolean } {
  if (isInsufficientCreditsError(error)) {
    const required = error.required ?? fallback?.required ?? 1;
    const balance = error.balance ?? fallback?.balance ?? 0;
    return {
      message: formatInsufficientCreditsMessage(strings, required, balance),
      showPricing: true,
    };
  }

  if (isConcurrentProcessingError(error) || isRateLimitError(error)) {
    return {
      message: strings.concurrentProcessing,
      showPricing: false,
    };
  }

  if (error instanceof Error && error.message) {
    return { message: error.message, showPricing: false };
  }

  return { message: strings.generationFailed, showPricing: false };
}
